<template>
  <!-- Slice 12 — post-sign guided wizard. Overlay modal that walks the patient
       through each INCOMPLETE consent form (embedded in an iframe — no new
       tabs/windows, ever) and then an optional payment step. Rendered as a
       fixed overlay so it sits on top of whichever page state is showing
       (the paper OR the post-sign success takeover). `.pv-no-print` keeps it
       out of the printout. -->
  <div v-if="open" class="nsw-overlay pv-no-print" @click.self="$emit('close')">
    <div class="nsw-dialog" role="dialog" aria-modal="true">
      <!-- Header: current title + an always-working close (never trap). -->
      <div class="nsw-header">
        <div class="nsw-title">{{ headerTitle }}</div>
        <button class="nsw-x" type="button" title="Close" @click="$emit('close')">✕</button>
      </div>

      <!-- Step strip — every step with its state (done ✓ / current / upcoming).
           The payment step renders muted until every consent step is ✓, so the
           emphasis stays sequential; it's still reachable once consents are
           done or skipped. -->
      <div v-if="steps.length > 1" class="nsw-steps">
        <div
          v-for="(s, i) in steps"
          :key="stepKey(s, i)"
          class="nsw-step"
          :class="stepClass(s, i)"
        >
          <span class="nsw-step-dot">
            <i v-if="isStepDone(s)" class="fa fa-check"></i>
            <span v-else>{{ i + 1 }}</span>
          </span>
          <span class="nsw-step-label">{{ stepShortLabel(s, i) }}</span>
        </div>
      </div>

      <!-- Body — the iframe form, the payment summary, or the closing state. -->
      <div class="nsw-body">
        <!-- Closing state — all consents done and no payment step. -->
        <div v-if="atCloseOut" class="nsw-allset">
          <div class="nsw-allset-icon"><i class="fa fa-check-circle"></i></div>
          <h3 class="nsw-allset-title">You're all set</h3>
          <p class="nsw-allset-sub">
            Thanks for completing your forms — your office has everything they need before your visit.
          </p>
          <button class="nsw-btn-primary" type="button" @click="$emit('close')">Done</button>
        </div>

        <!-- Payment (optional) — friendly summary, no iframe. -->
        <div v-else-if="currentStep && currentStep.kind === 'payment'" class="nsw-pay">
          <div class="nsw-pay-icon"><i class="fa fa-credit-card"></i></div>
          <h3 class="nsw-pay-title">One last optional step</h3>
          <p class="nsw-pay-sub">
            Making a payment now is completely optional — you can pay today or later at the office.
          </p>
          <div class="nsw-pay-figures">
            <div class="nsw-pay-fig">
              <span class="nsw-pay-fig-label">Paid so far</span>
              <span class="nsw-pay-fig-amt">{{ fmtMoney(paidTotal) }}</span>
            </div>
            <div class="nsw-pay-fig nsw-pay-fig-remain">
              <span class="nsw-pay-fig-label">Remaining</span>
              <span class="nsw-pay-fig-amt">{{ fmtMoney(remaining) }}</span>
            </div>
          </div>
          <div class="nsw-pay-actions">
            <button class="nsw-btn-primary" type="button" @click="$emit('go-to-payment')">
              Make a payment
            </button>
            <button class="nsw-btn-ghost" type="button" @click="$emit('close')">
              I'll pay at the office
            </button>
          </div>
        </div>

        <!-- Consent form — embedded in an iframe that scrolls internally and
             fills the body height. Keyed by formIDForWeb so switching steps
             loads the right form. -->
        <div v-else-if="currentStep && currentStep.kind === 'form'" class="nsw-form">
          <iframe
            :key="currentStep.formIDForWeb"
            :src="appendTxPlan(currentStep.shortUrl)"
            class="nsw-iframe"
            title="Consent form"
          ></iframe>
          <!-- Brief success beat after the form posts back, before auto-advance. -->
          <div v-if="showBeat" class="nsw-beat">
            <div class="nsw-beat-card">
              <i class="fa fa-check-circle"></i>
              <span>Saved!</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer — skip / back / next. Hidden on the closing state (its Done
           button lives in the body). On the payment step only Back shows; the
           pay actions live in the body. -->
      <div v-if="!atCloseOut" class="nsw-footer">
        <button
          v-if="currentStep && currentStep.kind === 'form'"
          class="nsw-skip"
          type="button"
          @click="onSkip"
        >Skip this form for now</button>
        <span class="nsw-footer-spacer"></span>
        <button v-if="canGoBack" class="nsw-back" type="button" @click="onBack">Back</button>
        <button
          v-if="currentStep && currentStep.kind === 'form'"
          class="nsw-next"
          type="button"
          :disabled="!currentStep.done"
          @click="onNext"
        >Next</button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PatientNextStepsWizard',

  props: {
    // Full consent-forms list from the patient-view payload. The wizard
    // snapshots the INCOMPLETE ones into local steps when it opens.
    consentForms: { type: Array, default: () => [] },
    // Whether the plan has been signed (drives the payment step's existence).
    signed: { type: Boolean, default: false },
    // Remaining balance (payment step only appears when the practice can take a
    // payment AND signed && remaining > 0).
    remaining: { type: Number, default: 0 },
    // Server-resolved per-site payments capability, threaded down from the
    // patient-view payload. Default false — default-deny, matching how
    // TxPatientView treats the same capability.
    //
    // Load-bearing: the payment step's CTA scrolls the parent to its deposit
    // panel, which is itself `v-if="paymentsEnabled"`. Deciding the step on
    // balance alone meant a payments-off practice offered "Make a payment",
    // found no panel to scroll to, and closed the wizard onto nothing. At a
    // practice with no consent mappings configured it was the ONLY step, so
    // signing opened a wizard whose single action did nothing at all.
    paymentsEnabled: { type: Boolean, default: false },
    // Amount paid so far — shown on the payment step summary.
    paidTotal: { type: Number, default: 0 },
    // Visibility. Parent owns this; the wizard emits `close` to request hiding.
    open: { type: Boolean, default: false },
    // When opening at a specific form (e.g. from the top banner button), the
    // formIDForWeb to land on first. Null = start at the first incomplete form.
    initialFormId: { type: [String, Number], default: null },
    // Treatment plan this wizard is scoped to. When a positive int, it's appended
    // to each consent form's iframe URL as ?txPlan=<n> so the submission is
    // stamped with the plan → plan-scoped consent completion in the Tx Presenter.
    treatPlanNum: { type: [Number, String], default: null },
  },

  data() {
    return {
      // Snapshot of the incomplete consent forms as step objects, captured when
      // the wizard opens so completing a form (which triggers a parent refetch)
      // doesn't reshuffle "Form N of M" or the current index mid-flow.
      formSteps: [],
      // Whether a final optional payment step exists (captured at open time).
      hasPaymentStep: false,
      // Index into `steps`. Equals steps.length when we've run past the end
      // (the closing "You're all set" state).
      currentIndex: 0,
      // formIDForWeb of the form that just posted back — drives the success
      // beat over the iframe for ~800ms before auto-advancing.
      justCompletedId: null,
    };
  },

  computed: {
    // Ordered steps: incomplete consent forms first, optional payment last.
    steps() {
      const arr = this.formSteps.slice();
      if (this.hasPaymentStep) arr.push({ kind: 'payment' });
      return arr;
    },
    currentStep() {
      return this.steps[this.currentIndex] || null;
    },
    // True once we've advanced past the last step with no payment step — the
    // closing "You're all set" state. Also true when there are no steps at all.
    atCloseOut() {
      return this.currentIndex >= this.steps.length;
    },
    // Every consent step is ✓ — un-mutes the payment step in the strip.
    allFormsDone() {
      return this.formSteps.length > 0 && this.formSteps.every((s) => s.done);
    },
    canGoBack() {
      return !this.atCloseOut && this.currentIndex > 0;
    },
    headerTitle() {
      if (this.atCloseOut) return "You're all set";
      const s = this.currentStep;
      if (!s) return 'Next steps';
      if (s.kind === 'payment') return 'Payment (optional)';
      // Form steps are first in `steps`, so currentIndex+1 is the form number.
      return `Form ${this.currentIndex + 1} of ${this.formSteps.length} — ${s.formName}`;
    },
    // Show the success beat only while the CURRENT step is the form that just
    // posted back. String-compare guards against string/number id mismatches.
    showBeat() {
      const s = this.currentStep;
      return !!(s && s.kind === 'form'
        && this.justCompletedId != null
        && String(this.justCompletedId) === String(s.formIDForWeb));
    },
  },

  watch: {
    // Rebuild the step snapshot each time the wizard opens (fresh incomplete
    // list + payment eligibility + landing index).
    open: {
      immediate: true,
      handler(val) {
        if (val) this.initSteps();
        else this.clearAdvanceTimer();
      },
    },
  },

  created() {
    // Non-reactive timer handle for the post-submit success beat / auto-advance.
    this.advanceTimer = null;
  },

  mounted() {
    // Same-origin postMessage from the embedded form on submit. Bound for the
    // component's lifetime; the handler ignores everything that doesn't match
    // the contract, so an always-on listener is harmless.
    window.addEventListener('message', this.onMessage);
  },

  beforeDestroy() {
    window.removeEventListener('message', this.onMessage);
    this.clearAdvanceTimer();
  },

  methods: {
    // Snapshot incomplete consent forms → local steps; decide payment step;
    // pick the landing index (initialFormId when provided and found).
    initSteps() {
      const incomplete = (this.consentForms || []).filter((f) => !f.completed);
      this.formSteps = incomplete.map((f) => ({
        kind: 'form',
        formIDForWeb: f.formIDForWeb,
        formName: f.formName || 'Consent form',
        shortUrl: f.shortUrl || '',
        done: false,
      }));
      this.hasPaymentStep = !!(this.paymentsEnabled && this.signed && this.remaining > 0);

      let idx = 0;
      if (this.initialFormId != null) {
        const i = this.formSteps.findIndex(
          (s) => String(s.formIDForWeb) === String(this.initialFormId),
        );
        if (i >= 0) idx = i;
      }
      this.currentIndex = idx;
      this.justCompletedId = null;
      this.clearAdvanceTimer();
    },

    // ─── Step-strip helpers ──────────────────────────────
    stepKey(s, i) {
      return s.kind === 'payment' ? 'payment' : (s.formIDForWeb != null ? 'f-' + s.formIDForWeb : 'f-' + i);
    },
    isStepDone(s) {
      return s.kind === 'form' && !!s.done;
    },
    stepShortLabel(s, i) {
      return s.kind === 'payment' ? 'Payment' : 'Form ' + (i + 1);
    },
    stepClass(s, i) {
      return {
        'is-done': this.isStepDone(s),
        'is-current': i === this.currentIndex,
        // Payment stays muted until every consent step is ✓ (unless it's the
        // current step, in which case emphasize it).
        'is-muted': s.kind === 'payment' && !this.allFormsDone && i !== this.currentIndex,
      };
    },

    // ─── Message handling ────────────────────────────────
    onMessage(event) {
      if (!this.open) return;
      // Per the shared contract: same origin + the exact event type.
      if (event.origin !== window.location.origin) return;
      const data = event.data;
      if (!data || data.type !== 'teamio:form-submitted') return;

      const step = this.formSteps.find(
        (s) => String(s.formIDForWeb) === String(data.formIDForWeb),
      );
      if (!step || step.done) return;

      step.done = true; // reactive — `done` key exists on the snapshot object
      this.$emit('form-completed', {
        formIDForWeb: data.formIDForWeb,
        formSubmissionIDForWeb: data.formSubmissionIDForWeb,
      });

      // Brief success beat, then auto-advance to the next incomplete step
      // (or the payment step / close-out).
      this.justCompletedId = data.formIDForWeb;
      this.clearAdvanceTimer();
      this.advanceTimer = setTimeout(() => {
        this.justCompletedId = null;
        this.advanceAfterComplete();
      }, 800);
    },

    advanceAfterComplete() {
      const nextForm = this.formSteps.findIndex((s) => !s.done);
      if (nextForm >= 0) {
        this.currentIndex = nextForm;
        return;
      }
      // All consent forms done — go to payment if present, else the close-out.
      this.currentIndex = this.hasPaymentStep ? this.steps.length - 1 : this.steps.length;
    },

    clearAdvanceTimer() {
      if (this.advanceTimer) {
        clearTimeout(this.advanceTimer);
        this.advanceTimer = null;
      }
    },

    // ─── Footer nav ──────────────────────────────────────
    onSkip() {
      // Advance without marking ✓.
      this.currentIndex = Math.min(this.currentIndex + 1, this.steps.length);
    },
    onNext() {
      // Only reachable when the current form is ✓ (button disabled otherwise).
      this.currentIndex = Math.min(this.currentIndex + 1, this.steps.length);
    },
    onBack() {
      if (this.currentIndex > 0) this.currentIndex -= 1;
    },

    // Build the iframe src for a form step, appending ?txPlan=<n> when this
    // wizard is scoped to a treatment plan (positive int only). Robust to a
    // shortUrl that already carries a query string, though today's /l/<id>
    // links have none.
    appendTxPlan(url) {
      if (!url) return url || '';
      const n = Number(this.treatPlanNum);
      if (!Number.isInteger(n) || n <= 0) return url;
      const sep = url.indexOf('?') === -1 ? '?' : '&';
      return url + sep + 'txPlan=' + n;
    },

    // Money formatter mirroring the patient page (toLocaleString, no cents),
    // but always renders a $ amount — including $0 for "Paid so far".
    fmtMoney(n) {
      const v = parseFloat(n) || 0;
      return '$' + v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
  },
};
</script>

<style scoped>
/* Match TxPatientView's Inter / #0f172a / teal (#0d5f54) visual language. */
.nsw-overlay {
  position: fixed;
  inset: 0;
  z-index: 4000;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  color: #0f172a;
  -webkit-font-smoothing: antialiased;
}
.nsw-overlay * { box-sizing: border-box; }

.nsw-dialog {
  width: min(920px, 94vw);
  height: min(86vh, 900px);
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.32);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Header */
.nsw-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid #e4e7ec;
  flex-shrink: 0;
}
.nsw-title {
  flex: 1;
  min-width: 0;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: -0.2px;
  color: #0f172a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.nsw-x {
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  border: none;
  background: #f1f3f6;
  border-radius: 8px;
  color: #475569;
  font-size: 13px;
  cursor: pointer;
  transition: background 0.12s, color 0.12s;
}
.nsw-x:hover { background: #e2e8f0; color: #0f172a; }

/* Step strip */
.nsw-steps {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border-bottom: 1px solid #eef1f4;
  overflow-x: auto;
  flex-shrink: 0;
}
.nsw-step {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  flex-shrink: 0;
  padding: 4px 10px 4px 4px;
  border-radius: 999px;
  color: #64748b;
}
.nsw-step-dot {
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  border-radius: 50%;
  background: #e2e8f0;
  color: #64748b;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
}
.nsw-step-label {
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
}
.nsw-step.is-current { background: #ecf5f3; color: #0d5f54; }
.nsw-step.is-current .nsw-step-dot { background: #0d5f54; color: #fff; }
.nsw-step.is-done .nsw-step-dot { background: #10b981; color: #fff; }
.nsw-step.is-done .nsw-step-label { color: #047857; }
.nsw-step.is-muted { opacity: 0.45; }

/* Body */
.nsw-body {
  position: relative;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
}

/* Form step — iframe fills, scrolls internally. */
.nsw-form {
  position: relative;
  flex: 1;
  min-height: 0;
}
.nsw-iframe {
  width: 100%;
  height: 100%;
  border: none;
  display: block;
}
.nsw-beat {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.82);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: nsw-beat-in 0.15s ease;
}
.nsw-beat-card {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 14px 22px;
  background: #fff;
  border: 1px solid #b9dcd5;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
  font-size: 15px;
  font-weight: 600;
  color: #047857;
}
.nsw-beat-card .fa { font-size: 20px; color: #10b981; }
@keyframes nsw-beat-in {
  from { opacity: 0; transform: scale(0.98); }
  to   { opacity: 1; transform: scale(1); }
}

/* Payment step + closing state share a centered column look. */
.nsw-pay,
.nsw-allset {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 32px 28px;
}
.nsw-pay-icon,
.nsw-allset-icon {
  width: 58px;
  height: 58px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
}
.nsw-pay-icon { background: #ecf5f3; color: #0d5f54; }
.nsw-pay-icon .fa { font-size: 24px; }
.nsw-allset-icon .fa { font-size: 58px; color: #10b981; }
.nsw-pay-title,
.nsw-allset-title {
  margin: 0 0 6px;
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 20px;
  font-weight: 600;
  color: #0d5f54;
}
.nsw-pay-sub,
.nsw-allset-sub {
  margin: 0 0 20px;
  max-width: 420px;
  font-size: 13.5px;
  color: #475569;
  line-height: 1.55;
}
.nsw-pay-figures {
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
}
.nsw-pay-fig {
  min-width: 130px;
  padding: 14px 18px;
  border: 1px solid #e4e7ec;
  border-radius: 10px;
  background: #f8fafc;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.nsw-pay-fig-remain { border-color: #b9dcd5; background: #ecf5f3; }
.nsw-pay-fig-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #6b7785;
  font-weight: 600;
}
.nsw-pay-fig-amt {
  font-size: 20px;
  font-weight: 700;
  color: #101820;
  font-variant-numeric: tabular-nums;
}
.nsw-pay-fig-remain .nsw-pay-fig-amt { color: #0d5f54; }
.nsw-pay-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  max-width: 300px;
}

/* Shared buttons */
.nsw-btn-primary {
  padding: 12px 20px;
  background: #0d5f54;
  border: 1px solid #0d5f54;
  border-radius: 10px;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.12s, border-color 0.12s;
}
.nsw-btn-primary:hover { background: #094840; border-color: #094840; }
.nsw-btn-ghost {
  padding: 12px 20px;
  background: transparent;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  color: #475569;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.12s, color 0.12s;
}
.nsw-btn-ghost:hover { background: #f1f3f6; color: #0f172a; }

/* Footer nav */
.nsw-footer {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 20px;
  border-top: 1px solid #e4e7ec;
  flex-shrink: 0;
}
.nsw-footer-spacer { flex: 1; }
.nsw-skip {
  background: none;
  border: none;
  padding: 4px 2px;
  font-size: 12.5px;
  color: #94a3b8;
  cursor: pointer;
  text-decoration: underline;
  font-family: inherit;
}
.nsw-skip:hover { color: #64748b; }
.nsw-back {
  padding: 9px 16px;
  background: #fff;
  border: 1px solid #d4cfc3;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  cursor: pointer;
  font-family: inherit;
}
.nsw-back:hover { background: #f1f3f6; color: #0f172a; }
.nsw-next {
  padding: 9px 20px;
  background: #0d5f54;
  border: 1px solid #0d5f54;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.12s, opacity 0.12s;
}
.nsw-next:hover:not(:disabled) { background: #094840; }
.nsw-next:disabled { opacity: 0.45; cursor: not-allowed; }

/* Full-screen on phones. */
@media (max-width: 600px) {
  .nsw-overlay { padding: 0; }
  .nsw-dialog {
    width: 100vw;
    height: 100vh;
    height: 100dvh;
    max-width: 100vw;
    border-radius: 0;
  }
}
</style>
